import React, { useState } from 'react';
import toast from 'react-hot-toast';

import { useAppDispatch, useAppSelector, useAppStore } from '@/lib/hooks';
import {
  resetQuizData,
  setActiveQuestionIndex,
  setAnswerData,
  setQuizData,
  setQuizName,
} from '@/lib/slices/quiz-slice';
import { setModalName } from '@/lib/slices/modal-slice';

import FormModal from '@/common/components/modals/general/FormModal';
import ConfirmationModal from '@/common/components/modals/general/ConfirmationModal';
import StandardInputText from '@/common/components/input/StandardInputText';
import StandardInputNumber from '@/common/components/input/StandardInputNumber';
import { AIIcon } from '@/assets/icons/button';

import { QuizGenService } from '@/services/course/creation/quiz-generation-service';
import { QuizGenResponse } from 'quiz-types';

function QuizGenModal() {
  const store = useAppStore();
  const dispatch = useAppDispatch();

  // Redux selectors
  const quizName = useAppSelector(() => store.getState().quiz.name);
  const questions = useAppSelector(() => store.getState().quiz.questions);
  const activeCourseData = useAppSelector(() => store.getState().course.activeCourseData);

  const [questionCount, setQuestionCount] = useState<number | undefined>(5);
  const [quizDifficulty, setQuizDifficulty] = useState<string>('');
  const [quizDetails, setQuizDetails] = useState<string>('');

  // Reset form state
  const resetFormState = () => {
    setQuestionCount(5);
    setQuizDifficulty('');
    setQuizDetails('');
  };

  // Handle modal close
  const handleClose = () => {
    resetFormState();
    dispatch(setModalName(''));
  };

  /**
   * Handles submission of quiz generate modal
   **/
  const handleSubmit = async () => {
    const quizData = await QuizGenService.generateQuizData(
      quizName,
      questionCount ?? 5,
      activeCourseData.name,
      quizDifficulty,
      quizDetails,
    );

    // Handle success or failure.
    if (!quizData) {
      handleError();
      throw new Error('Quiz generation failed');
    } else {
      handleSuccess(quizData);
      resetFormState();
    }
  };

  /**
   * Handles successful quiz generation
   * Update redux with response info
   */
  const handleSuccess = (quizData: QuizGenResponse): void => {
    dispatch(setActiveQuestionIndex(0));
    dispatch(setQuizData(quizData.questions));
    dispatch(setAnswerData(quizData.answers));
  };

  /**
   * Display error and handle reset of quiz gen modal if there is an
   * error during generation
   **/
  const handleError = () => {
    toast.error('There was an error generating your quiz. Please try again.');
  };

  // Form validation
  const isFormValid = () => {
    return quizName.trim() !== '' && questionCount !== undefined && quizDifficulty.trim() !== '';
  };

  // Handle question count input with validation
  const handleQuestionCountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = e.target.value.trim();

    if (rawValue === '') {
      setQuestionCount(undefined);
      return;
    }

    const parsed = parseInt(rawValue, 10);
    if (!isNaN(parsed) && parsed >= 1 && parsed <= 20) {
      setQuestionCount(parsed);
    }
  };

  // Handle quiz data replacement confirmation
  const handleConfirmReplace = async () => {
    const currentQuizName = quizName;
    dispatch(resetQuizData());
    dispatch(setQuizName(currentQuizName));
  };

  // Check if we should show confirmation modal
  const hasExistingQuizData = questions.length > 0 && questions[0]?.question;

  if (hasExistingQuizData) {
    return (
      <ConfirmationModal
        modalName='GenerateQuiz'
        title='Replace existing questions?'
        subtitle='Generating a quiz will replace the current questions you have created.'
        message='This action will permanently delete all current quiz questions and replace them with newly generated ones.'
        description='Make sure you have saved any important work before proceeding.'
        confirmText='Confirm'
        confirmIcon={<AIIcon className='w-[20px] h-[20px]' />}
        onConfirm={handleConfirmReplace}
        className='max-w-[400px] w-full'
        closeOnClickOff={true}
        skipAutoClose={true}
      />
    );
  }

  return (
    <FormModal
      modalName='GenerateQuiz'
      title='Generate New Quiz'
      subtitle='Fill the fields with the quiz information'
      className='max-w-[900px] w-full'
      closeOnClickOff={true}
      onSubmit={handleSubmit}
      submitButton={{
        text: 'Generate Quiz',
        icon: <AIIcon className='w-[20px] h-[20px] mr-2' />,
        disabled: !isFormValid(),
      }}
      loadingText='Generating your quiz...'
    >
      <StandardInputText
        id='quizName'
        label='Quiz Name'
        isRequired={true}
        placeholderText='Enter quiz name'
        value={quizName}
        onChange={(e) => dispatch(setQuizName(e.target.value))}
      />

      <StandardInputText
        id='courseName'
        label='Course Name'
        isRequired={true}
        value={activeCourseData.name}
        disabled={true}
        onChange={() => {}} // Disabled field, no-op
      />

      <StandardInputNumber
        id='questionCount'
        label='Question Count'
        isRequired={true}
        min={1}
        max={20}
        value={questionCount ?? ''}
        onChange={handleQuestionCountChange}
        placeholderText='Enter number of questions (max of 20)'
      />

      <StandardInputText
        id='quizDifficulty'
        label='Quiz Difficulty'
        isRequired={true}
        placeholderText='Example: 9th grade, College level, Working professional...'
        value={quizDifficulty}
        onChange={(e) => setQuizDifficulty(e.target.value)}
      />

      <StandardInputText
        id='quizDetails'
        label='Additional Details'
        placeholderText='Enter any general details or specifications to be used in the quiz generation'
        value={quizDetails}
        onChange={(e) => setQuizDetails(e.target.value)}
      />
    </FormModal>
  );
}

export default QuizGenModal;
